/**
 * The remaining status enums, as a person reads them.
 *
 * Attendance and call outcomes have their own modules — those two carry tones
 * and picker order as well as labels, and have enough around them to be worth
 * their own file. These three are labels and nothing else, so one module
 * rather than three of four lines each.
 *
 * All three were rendering as `status.replace(/_/g, ' ')`, which is the same
 * fault the call outcomes and submission statuses had: "partially paid" and
 * "yet to join" in columns beside properly cased names and clients. The enum
 * is a wire format; the column is prose.
 *
 * Each falls back to the raw spelling rather than to a blank, so a value added
 * to an enum before it is added here shows up readable and gets noticed,
 * instead of leaving an empty cell nobody can explain.
 */

/** Mirrors `PlacementStatus` in `app/models/enums.py`. */
export const PLACEMENT_STATUS_LABELS: Record<string, string> = {
  yet_to_join: 'Yet to Join',
  working: 'Working',
  drop: 'Drop',
  not_joined: 'Not Joined',
  rejected: 'Rejected',
  hold: 'On Hold',
  replacement_done: 'Replacement Done',
};

export function placementStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return PLACEMENT_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/** Mirrors `InvoiceStatus` in `app/models/enums.py`. */
export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  raised: 'Raised',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  written_off: 'Written Off',
};

export function invoiceStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return INVOICE_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * Mirrors `PingResponse` in `app/models/enums.py`.
 *
 * What somebody answered a check-in with. "Field visit" rather than "Field
 * Visit": these read as a sentence in the detail panel — "answered: field
 * visit" — rather than as a column heading.
 */
export const PING_RESPONSE_LABELS: Record<string, string> = {
  working: 'Working',
  meeting: 'Meeting',
  field_visit: 'Field visit',
  break: 'Break',
  commuting: 'Commuting',
  idle: 'Idle',
};

export function pingResponseLabel(
  response: string | null | undefined,
): string | null {
  if (!response) return null;
  return PING_RESPONSE_LABELS[response] ?? response.replace(/_/g, ' ');
}

/** Mirrors `EmployeeStatus` in `app/models/enums.py`. */
export const EMPLOYEE_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  on_notice: 'On Notice',
  offboarded: 'Offboarded',
};

export function employeeStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return EMPLOYEE_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * Mirrors `TrackerStatus` in `app/models/enums.py`.
 *
 * The derived ladder on the Joining Tracker -- never stored, resolved at read
 * time from the submission, interview, offer and placement behind each row.
 * These are the words the filter offers and the Status column prints, so they
 * have to read as the stages a recruiter talks about rather than as the enum.
 */
export const TRACKER_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  shortlisted: 'Shortlisted',
  interview_scheduled: 'Interview Scheduled',
  interviewed: 'Interviewed',
  selected: 'Selected',
  followup: 'Follow Up',
  offered: 'Offered',
  offer_declined: 'Offer Declined',
  offer_withdrawn: 'Offer Withdrawn',
  yet_to_join: 'Yet to Join',
  working: 'Working',
  hold: 'On Hold',
  rejected: 'Rejected',
  drop: 'Drop',
  not_joined: 'Not Joined',
  // The ladder's furthest rung: they dropped and somebody was supplied in
  // their place. Its own status rather than a remark on Drop, because the two
  // mean different things to the fee.
  replacement_done: 'Replacement Done',
};

export function trackerStatusLabel(
  status: string | null | undefined,
): string | null {
  if (!status) return null;
  return TRACKER_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}
